import { useParams, useNavigate } from 'react-router'
import { Button } from '@klear/ui/components/button'
import { ArrowLeft } from 'lucide-react'
import { PaymentStatusBadge } from '@klear/ui/components/payment-status-badge'
import { Label } from '@klear/ui/components/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@klear/ui/components/card'
import { PaymentNetworkOptions } from '@klear/payment/constants'
import { ROUTE_TRANSFERS } from '@klear/constants'

import { useTransfer } from '@/app/transfers/use-transfers'
import { paymentIsFailed } from '@klear/payment/status'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

export default function TransferDetail() {
  const { paymentId } = useParams<{ paymentId: string }>()
  const navigate = useNavigate()
  const paymentIdNum = parseInt(paymentId || '0', 10)

  const { transfer, isNonTransferPayment, isLoading, error } = useTransfer(paymentIdNum > 0 ? paymentIdNum : undefined)
  useDocumentTitle(transfer?.idDisplay, 'Transfer')

  if (isLoading) {
    return <div className="container mx-auto px-4 py-8 text-center">Loading transfer details...</div>
  }

  if (error) {
    return <div className="container mx-auto px-4 py-8 text-center text-red-500">Error loading transfer</div>
  }

  if (isNonTransferPayment) {
    return (
      <div className="container mx-auto px-4 py-8 text-center text-muted-foreground">
        This payment is not an internal transfer.
      </div>
    )
  }

  if (!transfer) {
    return <div className="container mx-auto px-4 py-8 text-center text-red-500">Error loading transfer</div>
  }

  return (
    <div className="flex flex-col space-y-8 p-4 md:flex">
      <div className="flex items-center justify-between space-y-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Transfer Details</h2>
          <p className="text-muted-foreground">Transfer ID: {transfer.id}</p>
        </div>
        <Button variant="outline" onClick={() => navigate(`/${ROUTE_TRANSFERS}`)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Transfers
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Transfer</CardTitle>
            <CardDescription>From, to, amount, and status</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-muted-foreground">Status</Label>
              <div className="mt-1">
                <PaymentStatusBadge status={transfer.status} />
              </div>
            </div>
            {paymentIsFailed(transfer.status) && (
              <div>
                <Label className="text-muted-foreground">Failure reason</Label>
                <div className="mt-1 font-medium text-destructive text-sm">{transfer.stripePaymentErrorMessage}</div>
              </div>
            )}
            <div>
              <Label className="text-muted-foreground">From</Label>
              <div className="font-medium">{transfer.sourceAccountOrganizationName || '—'}</div>
            </div>
            <div>
              <Label className="text-muted-foreground">To</Label>
              <div className="font-medium">{transfer.destinationAccountOrganizationName || '—'}</div>
            </div>
            <div>
              <Label className="text-muted-foreground">Amount</Label>
              <div className="font-medium">{transfer.amountFormatted}</div>
            </div>
            <div>
              <Label className="text-muted-foreground">Network</Label>
              <div className="font-medium">
                {PaymentNetworkOptions.find((n) => n.value === transfer.paymentNetwork)?.label ||
                  transfer.paymentNetwork}
              </div>
            </div>
            <div>
              <Label className="text-muted-foreground">Effective Date</Label>
              <div className="font-medium">{transfer.effectiveDate?.toString() ?? '-'}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Notes & References</CardTitle>
            <CardDescription>Stripe identifiers and free-text notes</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-muted-foreground">Payment Provider</Label>
              <div className="font-medium">{transfer.paymentProviderName}</div>
            </div>
            {transfer.stripePaymentId && (
              <div>
                <Label className="text-muted-foreground">Stripe Payment ID</Label>
                <div className="font-medium font-mono text-sm">{transfer.stripePaymentId}</div>
              </div>
            )}
            {transfer.notes && (
              <div>
                <Label className="text-muted-foreground">Notes</Label>
                <div className="mt-1 whitespace-pre-wrap">{transfer.notes}</div>
              </div>
            )}
            {transfer.internalNotes && (
              <div>
                <Label className="text-muted-foreground">Internal Notes</Label>
                <div className="mt-1 whitespace-pre-wrap">{transfer.internalNotes}</div>
              </div>
            )}
            <div>
              <Label className="text-muted-foreground">Created At</Label>
              <div className="font-medium">{transfer.createdAt?.toKlearDate().toString() ?? '-'}</div>
            </div>
            <div>
              <Label className="text-muted-foreground">Updated At</Label>
              <div className="font-medium">{transfer.updatedAt?.toKlearDate().toString() ?? '-'}</div>
            </div>
            {transfer.createdByEmail && (
              <div>
                <Label className="text-muted-foreground">Created By</Label>
                <div className="font-medium">{transfer.createdByEmail}</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
